import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  UserCredential,
  signInWithCredential,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { Platform } from 'react-native';
import { ERROR_MESSAGES } from './errorMessages';

// Your Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCJVc2oQmwsWBM6X4GdRqQ__1B6m9myLq0",
  authDomain: "astra-72712.firebaseapp.com",
  projectId: "astra-72712",
  storageBucket: "astra-72712.firebasestorage.app",
  messagingSenderId: "376215720045",
  appId: "1:376215720045:web:9d9e0631e13954f2caef64",
  measurementId: "G-2RM31XYQRX"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

export class AuthenticationService {
  static async signup(email: string, password: string, name: string): Promise<UserCredential> {
    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { uid } = userCredential.user;
      // Store additional user data in Firestore
      await setDoc(doc(db, 'users', uid), {
        name,
        email,
        birthDate: '',
        birthTime: '',
        birthPlace: '',
        zodiacSign: '',
        isProfileComplete: false,
      });
      // Also store in AsyncStorage for fast access
      await AsyncStorage.setItem('userProfile', JSON.stringify({
        name,
        email,
        birthDate: '',
        birthTime: '',
        birthPlace: '',
        zodiacSign: '',
        isProfileComplete: false,
      }));
      return userCredential;
    } catch (error: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Signup error:', error);
      }
      if (error && typeof error === 'object' && 'code' in error) {
        const err = error as { code: string; message?: string };
        switch (err.code) {
          case 'auth/configuration-not-found':
            throw new Error(ERROR_MESSAGES.AUTH.CONFIG_NOT_FOUND);
          case 'auth/email-already-in-use':
            throw new Error(ERROR_MESSAGES.AUTH.EMAIL_IN_USE);
          case 'auth/invalid-email':
            throw new Error(ERROR_MESSAGES.AUTH.INVALID_EMAIL);
          case 'auth/weak-password':
            throw new Error(ERROR_MESSAGES.AUTH.WEAK_PASSWORD);
          case 'auth/operation-not-allowed':
            throw new Error(ERROR_MESSAGES.AUTH.OPERATION_NOT_ALLOWED);
          default:
            throw new Error(err.message || ERROR_MESSAGES.AUTH.DEFAULT);
        }
      }
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error(ERROR_MESSAGES.AUTH.DEFAULT);
    }
  }

  static async login(email: string, password: string): Promise<UserCredential> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const { uid } = userCredential.user;
      // Fetch user profile from Firestore
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profile = userDoc.data();
        await AsyncStorage.setItem('userProfile', JSON.stringify(profile));
      }
      return userCredential;
    } catch (error: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error);
      }
      if (error && typeof error === 'object' && 'code' in error) {
        const err = error as { code: string; message?: string };
        switch (err.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            throw new Error(ERROR_MESSAGES.AUTH.USER_NOT_FOUND);
          case 'auth/invalid-email':
            throw new Error(ERROR_MESSAGES.AUTH.INVALID_EMAIL);
          case 'auth/too-many-requests':
            throw new Error(ERROR_MESSAGES.AUTH.TOO_MANY_REQUESTS);
          case 'auth/configuration-not-found':
            throw new Error(ERROR_MESSAGES.AUTH.CONFIG_NOT_FOUND);
          default:
            throw new Error(err.message || ERROR_MESSAGES.AUTH.DEFAULT);
        }
      }
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error(ERROR_MESSAGES.AUTH.DEFAULT);
    }
  }

  static async logout(): Promise<void> {
    try {
      await signOut(auth);
      await AsyncStorage.removeItem('userProfile');
    } catch (error: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error(ERROR_MESSAGES.AUTH.LOGOUT);
    }
  }

  static async getCurrentUser() {
    return auth.currentUser;
  }

  private static async saveUserProfile(uid: string, displayName: string | null, email: string | null) {
    // Firestore/AsyncStorage logic...
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', uid), {
        name: displayName,
        email,
        birthDate: '',
        birthTime: '',
        birthPlace: '',
        zodiacSign: '',
        isProfileComplete: false,
      });
    }
    await AsyncStorage.setItem('userProfile', JSON.stringify({
      name: displayName,
      email,
      birthDate: '',
      birthTime: '',
      birthPlace: '',
      zodiacSign: '',
      isProfileComplete: false,
    }));
  }

  static async signInWithGoogle(): Promise<UserCredential | null> {
    if (Platform.OS === 'web') {
      try {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const { uid, displayName, email } = result.user;
        await this.saveUserProfile(uid, displayName, email);
        return result;
      } catch (error: unknown) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Google sign-in error:', error);
        }
        if (error instanceof Error) {
          throw new Error(error.message);
        }
        throw new Error(ERROR_MESSAGES.AUTH.GOOGLE);
      }
    } else {
      try {
        const WebBrowser = await import('expo-web-browser');
        const Google = await import('expo-auth-session/providers/google');
        WebBrowser.maybeCompleteAuthSession();
        const config = {
          expoClientId: '376215720045-if84fotlddorkalvh0nfku2kfui61eq6.apps.googleusercontent.com',
          iosClientId: '376215720045-if84fotlddorkalvh0nfku2kfui61eq6.apps.googleusercontent.com',
          androidClientId: '376215720045-if84fotlddorkalvh0nfku2kfui61eq6.apps.googleusercontent.com',
          webClientId: '376215720045-if84fotlddorkalvh0nfku2kfui61eq6.apps.googleusercontent.com',
        };
        const [request, response, promptAsync] = Google.useAuthRequest(config);
        const result = await promptAsync();
        if (result.type === 'success' && result.authentication?.idToken) {
          const credential = GoogleAuthProvider.credential(result.authentication.idToken);
          const userCredential = await signInWithCredential(auth, credential);
          const { uid, displayName, email } = userCredential.user;
          await this.saveUserProfile(uid, displayName, email);
          return userCredential;
        }
        return null;
      } catch (error: unknown) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Google sign-in error:', error);
        }
        if (error instanceof Error) {
          throw new Error(error.message);
        }
        throw new Error(ERROR_MESSAGES.AUTH.GOOGLE);
      }
    }
  }

  static async signInWithGoogleWeb(): Promise<UserCredential | null> {
    try {
      if (Platform.OS === 'web') {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const { uid, displayName, email } = result.user;
        // Check if user exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (!userDoc.exists()) {
          // Store new user data in Firestore
          await setDoc(doc(db, 'users', uid), {
            name: displayName,
            email,
            birthDate: '',
            birthTime: '',
            birthPlace: '',
            zodiacSign: '',
            isProfileComplete: false,
          });
        }
        // Store in AsyncStorage
        await AsyncStorage.setItem('userProfile', JSON.stringify({
          name: displayName,
          email,
          birthDate: '',
          birthTime: '',
          birthPlace: '',
          zodiacSign: '',
          isProfileComplete: false,
        }));
        return result;
      }
      return null;
    } catch (error: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Google sign-in error:', error);
      }
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error(ERROR_MESSAGES.AUTH.GOOGLE);
    }
  }
} 
